import React, { useState, useEffect, useMemo } from "react";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import SearchableTable from "../../../components/Tables/SearchableTable";
import { CustomerColumns } from "../../../assets/Columns/CustomerColumns";
import CustomBtn from "../../../components/Buttons/CustomBtn";
import CreateCustomerModal from "../../../components/Modals/CreateCustomerModal";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomers } from "../../../store/Slices/CustomerSlice";
import CreateInvoiceModal from "../../../components/Modals/CreateInvoiceModal";
import { useNavigate } from "react-router-dom";
import EditCustomerModal from "../../../components/Modals/EditCustomerModal";
import DeleteModal from "../../../components/Modals/DeleteModal";
import { DeleteCustomerApi } from "../../../ApiRequests";
import { ErrorToast, SuccessToast } from "../../../utils/ShowToast";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import ExportToExcelButton from "../../../utils/ExportToExcel";
import PrintButton from "../../../components/Buttons/PrintButton";
import AddOpeningBalanceCustomerModal from "../../../components/Modals/AddOpeningBalanceCustomerModal";
import AddDirectDiscountModal from "../../../components/Modals/AddDirectDiscountModal";
import AddPaymentModal from "../../../components/Modals/AddPaymentModal";
import AddNonProductInvoiceModal from "../../../components/Modals/AddNonProductInvoiceModal";
import { filterTableRowsBySearch } from "../../../utils/tableSearch";
import MetricCard from "../../../components/Cards/MetricCard";
import EmptyState from "../../../components/ui/EmptyState";
import { FiUsers } from "react-icons/fi";

const getAppBasePath = () =>
  Number(localStorage.getItem("role")) === 1 ? "/admin" : "/branch";

const Customers = () => {
  const [OpenModal, setOpenModal] = useState(false);
  const [SearchText, setSearchText] = useState("");
  const [OpenInvoiceModal, setOpenInvoiceModal] = useState(false);
  const [OpenEditModal, setOpenEditModal] = useState(false);
  const [OpenDeleteModal, setOpenDeleteModal] = useState(false);
  const [Selected, setSelected] = useState("");
  const [Loading, setLoading] = useState(false);

  const [OpenOpeningBalance, setOpenOpeningBalance] = useState(false);
  const [OpeningBalanceCustomer, setOpeningBalanceCustomer] = useState(null);
  const [OpenDiscountModal, setOpenDiscountModal] = useState(false);
  const [OpenPaymentModal, setOpenPaymentModal] = useState(false);
  const [OpenNonProductModal, setOpenNonProductModal] = useState(false);

  const navigate = useNavigate();

  const dispatch = useDispatch();
  const Customers = useSelector((state) => state.Customers);

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  const summary = useMemo(() => {
    if (!Customers.data || Customers.data.length === 0) {
      return {
        total: 0,
        receivable: 0,
        received: 0,
      };
    }

    return Customers.data.reduce(
      (acc, customer) => ({
        total: acc.total + (customer.total || 0),
        receivable: acc.receivable + (customer.recieveable || 0),
        received: acc.received + (customer.recieved || 0),
      }),
      {
        total: 0,
        receivable: 0,
        received: 0,
      }
    );
  }, [Customers.data]);

  const filteredCustomers = useMemo(
    () => filterTableRowsBySearch(Customers.data, SearchText, CustomerColumns),
    [Customers.data, SearchText]
  );

  return (
    <BodyWrapper>
      <Header title="Customers" desc="Overview of your customer management">
        <CustomBtn
          title={"Add Opening Balance"}
          onClick={() => {
            setOpeningBalanceCustomer(null);
            setOpenOpeningBalance(true);
          }}
        />
        <CustomBtn
          title={"Add New Customer"}
          onClick={() => {
            setOpenModal(true);
          }}
        />
        <CustomBtn
          title={"Discount log"}
          onClick={() => navigate(`${getAppBasePath()}/discount-log`)}
        />
      </Header>
      <div className="flex justify-end gap-x-2">
        <PrintButton
          onClick={() => {
            navigate(`${getAppBasePath()}/customers-report`);
          }}
          title="Customers Report"
        />
        <ExportToExcelButton
          data={filteredCustomers}
          fileName={"Customers"}
        />
      </div>
      <div className="w-full mt-6 flex flex-col items-center">
        {Customers.loading ? (
          <div className="flex flex-1 justify-center items-center py-16">
            <FetchingLoading />
          </div>
        ) : Customers.data.length !== 0 ? (
          <div className="w-full">
              <SearchableTable
                setOpenEditModal={setOpenEditModal}
                setOpenDeleteModal={setOpenDeleteModal}
                setSelected={setSelected}
                onAddDiscount={(row) => {
                  setSelected(row);
                  setOpenDiscountModal(true);
                }}
                onViewDiscountLog={(row) => {
                  navigate(
                    `${getAppBasePath()}/discount-log/customer/${row._id}`
                  );
                }}
                onViewLedger={(row) => {
                  navigate(`${getAppBasePath()}/ledger`, {
                    state: { customer: row },
                  });
                }}
                onOpeningBalance={(row) => {
                  setOpeningBalanceCustomer(row);
                  setOpenOpeningBalance(true);
                }}
                onAddPayment={(row) => {
                  setSelected(row);
                  setOpenPaymentModal(true);
                }}
                onViewPayments={(row) => {
                  navigate(`${getAppBasePath()}/customers-payments/${row._id}`);
                }}
                onAddNonProductInvoice={(row) => {
                  setSelected(row);
                  setOpenNonProductModal(true);
                }}
                SearchPlaceholder={"Search customers..."}
                SearchText={SearchText}
                setSearchText={setSearchText}
                CurrentData={Customers.data}
                Columns={CustomerColumns}
              />
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                <MetricCard
                  title="Total Amount"
                  value={`Rs ${summary.total.toLocaleString("en-PK")}`}
                  subtitle="All-time customer billed amount"
                  tone="neutral"
                />
                <MetricCard
                  title="Total Receivable"
                  value={`Rs ${summary.receivable.toLocaleString("en-PK")}`}
                  subtitle="Pending amount from customers"
                  tone="danger"
                />
                <MetricCard
                  title="Total Received"
                  value={`Rs ${summary.received.toLocaleString("en-PK")}`}
                  subtitle="Collected payments from customers"
                  tone="success"
                />
              </div>
            </div>
        ) : (
          <EmptyState
            icon={FiUsers}
            title="No customers yet"
            description="Add your first customer to start creating invoices and tracking payments."
          />
        )}
      </div>
      {OpenModal && (
        <CreateCustomerModal Open={OpenModal} setOpen={setOpenModal} />
      )}
      {OpenInvoiceModal && (
        <CreateInvoiceModal
          Open={OpenInvoiceModal}
          setOpen={setOpenInvoiceModal}
        />
      )}
      {OpenOpeningBalance && (
        <AddOpeningBalanceCustomerModal
          Open={OpenOpeningBalance}
          setOpen={setOpenOpeningBalance}
          initialCustomer={OpeningBalanceCustomer}
        />
      )}
      {OpenEditModal && (
        <EditCustomerModal
          Open={OpenEditModal}
          setOpen={setOpenEditModal}
          currentCustomer={Selected}
        />
      )}
      {OpenDiscountModal && Selected && (
        <AddDirectDiscountModal
          Open={OpenDiscountModal}
          setOpen={(open) => {
            setOpenDiscountModal(open);
            if (!open) setSelected("");
          }}
          userType={1}
          entity={Selected}
          onSuccess={() => dispatch(fetchCustomers())}
        />
      )}
      {OpenPaymentModal && Selected && (
        <AddPaymentModal
          Open={OpenPaymentModal}
          setOpen={(open) => {
            setOpenPaymentModal(open);
            if (!open) setSelected("");
          }}
          presetEntityType="customer"
          presetEntity={Selected}
        />
      )}
      {OpenNonProductModal && Selected && (
        <AddNonProductInvoiceModal
          Open={OpenNonProductModal}
          setOpen={(open) => {
            setOpenNonProductModal(open);
            if (!open) setSelected("");
          }}
          userType={1}
          entity={Selected}
          onSuccess={() => dispatch(fetchCustomers())}
        />
      )}
      {OpenDeleteModal && (
        <DeleteModal
          Open={OpenDeleteModal}
          setOpen={setOpenDeleteModal}
          Loading={Loading}
          onSubmit={async () => {
            setLoading(true);
            try {
              const response = await DeleteCustomerApi(Selected._id);
              if (response.data.success) {
                setOpenDeleteModal(false);
                SuccessToast("Customer delete successfully");
                dispatch(fetchCustomers());
              } else {
                ErrorToast(response.data.error?.msg);
              }
            } catch (err) {
              ErrorToast(
                err.response.data.error.msg || "Failed to delete customer!"
              );
            }
            setLoading(false);
          }}
        />
      )}
    </BodyWrapper>
  );
};

export default Customers;
